import {
  ATTACHMENT_CATEGORY_GROUPS,
  type AttachmentCategory,
} from "src/components/llm/utils/attachment-presentation";

/** Files that came from no folder, here and on the asset review card. */
export const LOOSE_FILES_LABEL = "No folder";

export type FileGroup<T> = { key: string; label: string; items: T[] };

/**
 * A dropped folder is the user's own grouping, so files that came from folders
 * are listed by folder; anything else is listed by type.
 */
export const groupFileItems = <
  T extends { category: AttachmentCategory; folderPath?: string },
>(
  items: T[],
): FileGroup<T>[] => {
  if (!items.some((item) => item.folderPath)) {
    return ATTACHMENT_CATEGORY_GROUPS.map((group) => ({
      key: group.category,
      label: group.label,
      items: items.filter((item) => item.category === group.category),
    })).filter((group) => group.items.length > 0);
  }
  const byFolder = new Map<string, T[]>();
  for (const item of items) {
    const key = item.folderPath ?? "";
    const folder = byFolder.get(key);
    if (folder) folder.push(item);
    else byFolder.set(key, [item]);
  }
  return [...byFolder.entries()]
    .sort(([a], [b]) =>
      // Loose files last, after every folder.
      a === "" ? 1 : b === "" ? -1 : a.localeCompare(b),
    )
    .map(([folderPath, folderItems]) => ({
      key: `folder:${folderPath}`,
      label: folderPath || LOOSE_FILES_LABEL,
      items: folderItems,
    }));
};

/**
 * The first `limit` items across groups, in order, dropping groups left empty.
 * Each group keeps its full `total` so headers still count every file.
 */
export const limitGroupItems = <T>(
  groups: FileGroup<T>[],
  limit: number,
): (FileGroup<T> & { total: number })[] => {
  let remaining = limit;
  const limited: (FileGroup<T> & { total: number })[] = [];
  for (const group of groups) {
    if (remaining <= 0) break;
    limited.push({
      ...group,
      items: group.items.slice(0, remaining),
      total: group.items.length,
    });
    remaining -= group.items.length;
  }
  return limited;
};
